import * as i0 from '@angular/core';
import { Component, Input, NgModule } from '@angular/core';

/*
* (c) Copyright Ascensio System SIA 2023
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*     http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/
const loadScript = async (url, id) => {
    return new Promise((resolve, reject) => {
        try {
            if (document.getElementById(id)) {
                if (window.DocsAPI)
                    return resolve(null);
                let intervalHandler = setInterval(() => {
                    if (!window.DocsAPI)
                        return;
                    clearInterval(intervalHandler);
                    return resolve(null);
                }, 500);
            }
            else {
                const script = document.createElement("script");
                script.setAttribute("type", "text/javascript");
                script.setAttribute("id", id);
                script.onload = resolve;
                script.onerror = reject;
                script.src = url;
                script.async = true;
                document.body.appendChild(script);
            }
        }
        catch (e) {
            console.error(e);
        }
    });
};

/*
* (c) Copyright Ascensio System SIA 2023
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*     http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/
class DocumentEditorComponent {
    constructor() {
        this.isFirstOnChanges = true;
        this.onLoad = () => {
            try {
                if (!window.DocsAPI)
                    this.onError(-3);
                if (window?.DocEditor?.instances[this.id]) {
                    console.log("Skip loading. Instance already exists", this.id);
                    return;
                }
                if (!window?.DocEditor?.instances) {
                    window.DocEditor = { instances: {} };
                }
                let initConfig = Object.assign({
                    document: {
                        fileType: this.document_fileType,
                        title: this.document_title,
                    },
                    documentType: this.documentType,
                    editorConfig: {
                        lang: this.editorConfig_lang,
                    },
                    events: {
                        onAppReady: this.onAppReady,
                        onDocumentStateChange: this.events_onDocumentStateChange,
                        onMetaChange: this.events_onMetaChange,
                        onDocumentReady: this.events_onDocumentReady,
                        onInfo: this.events_onInfo,
                        onWarning: this.events_onWarning,
                        onError: this.events_onError,
                        onRequestSharingSettings: this.events_onRequestSharingSettings,
                        onRequestRename: this.events_onRequestRename,
                        onMakeActionLink: this.events_onMakeActionLink,
                        onRequestInsertImage: this.events_onRequestInsertImage,
                        onRequestSaveAs: this.events_onRequestSaveAs,
                        onRequestMailMergeRecipients: this.events_onRequestMailMergeRecipients,
                        onRequestCompareFile: this.events_onRequestCompareFile,
                        onRequestEditRights: this.events_onRequestEditRights,
                        onRequestHistory: this.events_onRequestHistory,
                        onRequestHistoryClose: this.events_onRequestHistoryClose,
                        onRequestHistoryData: this.events_onRequestHistoryData,
                        onRequestRestore: this.events_onRequestRestore
                    },
                    height: this.height,
                    type: this.type,
                    width: this.width,
                }, this.config || {});
                const editor = window.DocsAPI.DocEditor(this.id, initConfig);
                window.DocEditor.instances[this.id] = editor;
            }
            catch (err) {
                console.error(err);
                this.onError(-1);
            }
        };
        this.onError = (errorCode) => {
            let message;
            switch (errorCode) {
                case -2:
                    message = "Error load DocsAPI from " + this.documentServerUrl;
                    break;
                case -3:
                    message = "DocsAPI is not defined";
                    break;
                default:
                    message = "Unknown error loading component";
                    errorCode = -1;
            }
            if (typeof this.onLoadComponentError == "undefined") {
                console.error(message);
            }
            else {
                this.onLoadComponentError(errorCode, message);
            }
        };
    }
    ngOnInit() {
        let url = this.documentServerUrl;
        if (!url.endsWith("/"))
            url += "/";
        const docApiUrl = `${url}web-apps/apps/api/documents/api.js`;
        loadScript(docApiUrl, "onlyoffice-api-script")
            .then(() => this.onLoad())
            .catch((err) => {
            this.onError(-2);
        });
    }
    ngOnChanges(changes) {
        const listNameChanges = ["config", "document_fileType", "document_title", "documentType", "editorConfig_lang", "height", "type", "width"];
        if (this.isFirstOnChanges) {
            this.isFirstOnChanges = false;
            return;
        }
        for (const name of listNameChanges) {
            if (changes.hasOwnProperty(name)) {
                if (window?.DocEditor?.instances[this.id]) {
                    window.DocEditor.instances[this.id].destroyEditor();
                    window.DocEditor.instances[this.id] = undefined;
                    console.log("Important props have been changed. Load new Editor.");
                    this.onLoad();
                    return;
                }
            }
        }
    }
    ngOnDestroy() {
        if (window?.DocEditor?.instances[this.id]) {
            window.DocEditor.instances[this.id].destroyEditor();
            window.DocEditor.instances[this.id] = undefined;
        }
    }
    onAppReady() {
        this.events_onAppReady(window.DocEditor.instances[this.id]);
    }
}
DocumentEditorComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.2", ngImport: i0, type: DocumentEditorComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
DocumentEditorComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.2", type: DocumentEditorComponent, selector: "document-editor", inputs: { id: "id", documentServerUrl: "documentServerUrl", config: "config", document_fileType: "document_fileType", document_title: "document_title", documentType: "documentType", editorConfig_lang: "editorConfig_lang", height: "height", type: "type", width: "width", onLoadComponentError: "onLoadComponentError", events_onAppReady: "events_onAppReady", events_onDocumentStateChange: "events_onDocumentStateChange", events_onMetaChange: "events_onMetaChange", events_onDocumentReady: "events_onDocumentReady", events_onInfo: "events_onInfo", events_onWarning: "events_onWarning", events_onError: "events_onError", events_onRequestSharingSettings: "events_onRequestSharingSettings", events_onRequestRename: "events_onRequestRename", events_onMakeActionLink: "events_onMakeActionLink", events_onRequestInsertImage: "events_onRequestInsertImage", events_onRequestSaveAs: "events_onRequestSaveAs", events_onRequestMailMergeRecipients: "events_onRequestMailMergeRecipients", events_onRequestCompareFile: "events_onRequestCompareFile", events_onRequestEditRights: "events_onRequestEditRights", events_onRequestHistory: "events_onRequestHistory", events_onRequestHistoryClose: "events_onRequestHistoryClose", events_onRequestHistoryData: "events_onRequestHistoryData", events_onRequestRestore: "events_onRequestRestore" }, usesOnChanges: true, ngImport: i0, template: '<div [id]="id"></div>', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.2", ngImport: i0, type: DocumentEditorComponent, decorators: [{
            type: Component,
            args: [{ selector: 'document-editor', template: '<div [id]="id"></div>' }]
        }], ctorParameters: function () { return []; }, propDecorators: { id: [{
                type: Input
            }], documentServerUrl: [{
                type: Input
            }], config: [{
                type: Input
            }], document_fileType: [{
                type: Input
            }], document_title: [{
                type: Input
            }], documentType: [{
                type: Input
            }], editorConfig_lang: [{
                type: Input
            }], height: [{
                type: Input
            }], type: [{
                type: Input
            }], width: [{
                type: Input
            }], onLoadComponentError: [{
                type: Input
            }], events_onAppReady: [{
                type: Input
            }], events_onDocumentStateChange: [{
                type: Input
            }], events_onMetaChange: [{
                type: Input
            }], events_onDocumentReady: [{
                type: Input
            }], events_onInfo: [{
                type: Input
            }], events_onWarning: [{
                type: Input
            }], events_onError: [{
                type: Input
            }], events_onRequestSharingSettings: [{
                type: Input
            }], events_onRequestRename: [{
                type: Input
            }], events_onMakeActionLink: [{
                type: Input
            }], events_onRequestInsertImage: [{
                type: Input
            }], events_onRequestSaveAs: [{
                type: Input
            }], events_onRequestMailMergeRecipients: [{
                type: Input
            }], events_onRequestCompareFile: [{
                type: Input
            }], events_onRequestEditRights: [{
                type: Input
            }], events_onRequestHistory: [{
                type: Input
            }], events_onRequestHistoryClose: [{
                type: Input
            }], events_onRequestHistoryData: [{
                type: Input
            }], events_onRequestRestore: [{
                type: Input
            }] } });

/*
* (c) Copyright Ascensio System SIA 2023
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*     http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/
class DocumentEditorModule {
}
DocumentEditorModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.2", ngImport: i0, type: DocumentEditorModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DocumentEditorModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.2", ngImport: i0, type: DocumentEditorModule, declarations: [DocumentEditorComponent], exports: [DocumentEditorComponent] });
DocumentEditorModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.2", ngImport: i0, type: DocumentEditorModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.2", ngImport: i0, type: DocumentEditorModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        DocumentEditorComponent
                    ],
                    imports: [],
                    exports: [
                        DocumentEditorComponent
                    ]
                }]
        }] });

/*
* (c) Copyright Ascensio System SIA 2023
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*     http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

/*
* (c) Copyright Ascensio System SIA 2023
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*     http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

/**
 * Generated bundle index. Do not edit.
 */

export { DocumentEditorComponent, DocumentEditorModule };
